import React, { useState, useEffect, useRef } from 'react';
import {
	FluxButton,
	FluxFind,
	FluxDropdown,
	FluxList,
	FluxListItem,
	FluxToolbar,
} from '@meltwater/flux-web-components-react';
import {
	Box,
	Paper,
	List,
	ListItem,
	ListItemText,
	ListItemButton,
	Divider,
	IconButton,
	Typography,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogContentText,
	DialogActions
} from '@mui/material';
import {
	History as HistoryIcon,
	Clear as ClearIcon
} from '@mui/icons-material';
import searchHistoryUtils from '../../utils/searchHistory';

const JiraSearch = () => {
	const [findvalue, setFindvalue] = useState('');
	const [showHistory, setShowHistory] = useState(false);
	const [searchHistory, setSearchHistory] = useState([]);
	const [showClearConfirm, setShowClearConfirm] = useState(false);
	const [itemToDelete, setItemToDelete] = useState(null);
	const searchRef = useRef(null);
	const historyRef = useRef(null);

	useEffect(() => {
		setSearchHistory(searchHistoryUtils.getHistory());
	}, []);

	useEffect(() => {
		const handleClickOutside = (event) => {
			if (
				searchRef.current && 
				!searchRef.current.contains(event.target) &&
				historyRef.current && 
				!historyRef.current.contains(event.target)
			) {
				setShowHistory(false);
			}
		};

		document.addEventListener('mousedown', handleClickOutside);
		return () => document.removeEventListener('mousedown', handleClickOutside);
	}, []);

	// Function to validate ticket format - allow any input with a dash for future flexibility
	const isValidTicket = (value) => {
		return value.includes('-');
	};
	
	const detectSearchType = (value) => {
		if (!value || value.trim() === '') return null;
		const trimmedValue = value.trim();

		if (isValidTicket(trimmedValue)) {
			return 'ticket';
		}
		
		const salesforcePattern = /^[a-zA-Z0-9]{15,18}$/;
		if (salesforcePattern.test(trimmedValue) && trimmedValue.length >= 15) {
			return 'salesforce';
		}
		
		// Check if it's numeric (Account/Opportunity ID)
		const numericPattern = /^\d{5,}$/;
		if (numericPattern.test(trimmedValue)) {
			return 'account'; // Can be either account or opportunity - backend will handle both
		}
		
		return null;
	};

	// Get the detected search type for current input
	const detectedType = detectSearchType(findvalue);

	const handleInputChange = (e) => {
		setFindvalue(e.target.value);
	};

	const handleKeyDown = (e) => {
		if (e.key === 'Enter') {
			const searchType = detectSearchType(findvalue);
			if (searchType) {
				handleSearch(searchType === 'ticket' ? 'ticket' : 
							 searchType === 'salesforce' ? 'SalesforceId' :
							 searchType === 'account' ? 'AccountID' : null);
			}
		}
	};

	const handleSearch = (id) => {
		if (!findvalue.trim()) return;

		let myHref;
		let searchType;
		
		switch (id) {
			case 'OpportunityID':
				myHref = `/jira/relatedtickets/${findvalue}/OpportunityID`;
				searchType = 'opportunity';
				break;
			case 'AccountID':
				myHref = `/jira/relatedtickets/${findvalue}/AccountID`;
				searchType = 'account';
				break;
			case 'SalesforceId':
				myHref = `/jira/salesforce/${findvalue}`;
				searchType = 'salesforce';
				break;
			case 'ticket':
				myHref = `/jira/${findvalue}`;
				searchType = 'ticket';
				break;
			default:
				return;
		}

		// Add to search history
		searchHistoryUtils.addToHistory(searchType, findvalue.trim());
		setSearchHistory(searchHistoryUtils.getHistory());
		
		// Validate and sanitize the URL
		try {
			if (typeof myHref !== 'string' || !myHref.startsWith('/jira/')) {
				console.error('Invalid URL format');
				return;
			}

			// Use encodeURI to properly encode the URL components
			const sanitizedUrl = encodeURI(myHref);
			
			// Use assign for navigation
			window.location.assign(sanitizedUrl);
		} catch (error) {
			console.error('Error processing URL:', error);
		}
	};

	const handleHistorySelect = (historyItem) => {
		setFindvalue(historyItem.value);
		setShowHistory(false);
		
		searchHistoryUtils.updateAccessTime(historyItem.id);
		setSearchHistory(searchHistoryUtils.getHistory());

		// Validate and sanitize the URL
		try {
			// Ensure the URL is relative and starts with /jira/
			if (typeof historyItem.searchUrl !== 'string' || 
				!historyItem.searchUrl.startsWith('/jira/')) {
				console.error('Invalid URL format');
				return;
			}

			// Use encodeURI to properly encode the URL components
			const sanitizedUrl = encodeURI(historyItem.searchUrl);
			
			// Use router push or assign to new location
			window.location.assign(sanitizedUrl);
		} catch (error) {
			console.error('Error processing URL:', error);
		}
	};

	const handleSearchInputFocus = () => {
		const history = searchHistoryUtils.getHistory();
		setSearchHistory(history);
		setShowHistory(history.length > 0);
	};

	const handleSearchInputBlur = (e) => {
		// Add a small delay to allow for dropdown clicks
		setTimeout(() => {
			if (!historyRef.current?.contains(document.activeElement)) {
				setShowHistory(false);
			}
		}, 150);
	};

	useEffect(() => {
		document.title = 'Jira Find';
		return () => {
			document.title = 'GSD Support Admin';
		};
	}, []);

	return (
		<Box sx={{ position: 'relative' }}>
			<div
				style={{ display: 'flex', justifyContent: 'flex-start', overflow: 'hidden' }}
			>
				<FluxToolbar>
					<FluxFind
						ref={searchRef}
						style={{
							border: '2px solid #28bcbb',
							borderRadius: '25px',
							padding: '8px 16px',
							outline: 'none',
							transition: 'all 0.3s ease',
							boxShadow: '0 2px 8px rgba(40, 188, 187, 0.15)',
							backgroundColor: '#ffffff'
						}}
						onFocus={(e) => {
							e.target.style.borderColor = '#1e9b9b';
							e.target.style.boxShadow = '0 4px 12px rgba(40, 188, 187, 0.25)';
							handleSearchInputFocus(e);
						}}
						onBlur={(e) => {
							e.target.style.borderColor = '#28bcbb';
							e.target.style.boxShadow = '0 2px 8px rgba(40, 188, 187, 0.15)';
							handleSearchInputBlur(e);
						}}
						value={findvalue}
						onInput={handleInputChange}
						onKeyDown={handleKeyDown}
					></FluxFind>
					<FluxDropdown hoistMenu>
						<FluxButton type='filled' slot='trigger' tint='primary' caret>
							Search By
						</FluxButton>
						<FluxList>
							<FluxListItem>
								<FluxButton
									disabled={detectedType !== 'ticket'}
									tint='primary'
									type='text'
									onClick={() => handleSearch('ticket')}
									id='ticket'
								>
									Ticket {detectedType === 'ticket' ? '✓' : ''}
								</FluxButton>
							</FluxListItem>
							<FluxListItem>
								<FluxButton
									disabled={detectedType !== 'account'}
									tint='primary'
									type='text'
									onClick={() => handleSearch('AccountID')}
									id='AccountID'
								>
									Account ID {detectedType === 'account' ? '✓' : ''}
								</FluxButton>
							</FluxListItem>
							<FluxListItem>
								<FluxButton
									disabled={detectedType !== 'account'}
									tint='primary'
									type='text'
									onClick={() => handleSearch('OpportunityID')}
									id='OpportunityID'
								>
									Opportunity ID {detectedType === 'account' ? '✓' : ''}
								</FluxButton>
							</FluxListItem>
							<FluxListItem>
								<FluxButton
									disabled={detectedType !== 'salesforce'}
									tint='primary'
									type='text'
									onClick={() => handleSearch('SalesforceId')}
									id='SalesforceId'
								>
									Salesforce ID {detectedType === 'salesforce' ? '✓' : ''}
								</FluxButton>
							</FluxListItem>
						</FluxList>
					</FluxDropdown>
				</FluxToolbar>
			</div>

			{showHistory && searchHistory.length > 0 && (
				<Paper
					ref={historyRef}
					elevation={8}
					sx={{
						position: 'absolute',
						top: '100%',
						left: 0,
						right: 0,
						zIndex: 1300,
						mt: 1,
						maxHeight: '300px',
						overflow: 'hidden',
						borderRadius: 2,
						border: '1px solid',
						borderColor: 'divider'
					}}
				>
					{/* Header */}
					<Box
						sx={{
							p: 2,
							pb: 1,
							background: 'grey.50',
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'space-between'
						}}
					>
						<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
							<HistoryIcon fontSize="small" color="action" />
							<Typography variant="subtitle2" color="text.secondary">
								Recent Searches
							</Typography>
						</Box>
						<IconButton
							size="small"
							onClick={() => setShowClearConfirm(true)}
							title="Clear all history"
						>
							<ClearIcon fontSize="small" />
						</IconButton>
					</Box>

					<Divider />

					{/* Scrollable History List */}
					<List
						sx={{
							maxHeight: '250px',
							overflow: 'auto',
							py: 0
						}}
					>
						{searchHistory.map((item) => (
							<ListItem
								key={item.id}
								disablePadding
								secondaryAction={
									<IconButton
										size="small"
										onClick={(e) => {
											e.stopPropagation();
											setItemToDelete(item.id);
										}}
										title="Remove from history"
									>
										<ClearIcon fontSize="small" />
									</IconButton>
								}
							>
								<ListItemButton
									onClick={() => handleHistorySelect(item)}
									sx={{ pr: 6 }}
								>
									<ListItemText
										primary={`${item.type.charAt(0).toUpperCase() + item.type.slice(1)}: ${item.value}`}
										secondary={searchHistoryUtils.getTimeAgo(item.timestamp)}
										primaryTypographyProps={{
											fontSize: '0.875rem',
											fontWeight: 500
										}}
										secondaryTypographyProps={{
											fontSize: '0.75rem'
										}}
									/>
								</ListItemButton>
							</ListItem>
						))}
					</List>
				</Paper>
			)}

			{/* Clear All Confirmation Dialog */}
			<Dialog
				open={showClearConfirm}
				onClose={() => setShowClearConfirm(false)}
				aria-labelledby="clear-dialog-title"
				aria-describedby="clear-dialog-description"
			>
				<DialogTitle id="clear-dialog-title">
					Clear Search History
				</DialogTitle>
				<DialogContent>
					<DialogContentText id="clear-dialog-description">
						Are you sure you want to clear all search history? This action cannot be undone.
					</DialogContentText>
				</DialogContent>
				<DialogActions>
					<FluxButton type="text" onClick={() => setShowClearConfirm(false)}>
						Cancel
					</FluxButton>
					<FluxButton 
						type="filled" 
						tint="danger" 
						onClick={() => {
							searchHistoryUtils.clearHistory();
							setSearchHistory([]);
							setShowHistory(false);
							setShowClearConfirm(false);
						}}
					>
						Clear All
					</FluxButton>
				</DialogActions>
			</Dialog>

			{/* Delete Item Confirmation Dialog */}
			<Dialog
				open={Boolean(itemToDelete)}
				onClose={() => setItemToDelete(null)}
				aria-labelledby="delete-dialog-title"
				aria-describedby="delete-dialog-description"
			>
				<DialogTitle id="delete-dialog-title">
					Remove Search Item
				</DialogTitle>
				<DialogContent>
					<DialogContentText id="delete-dialog-description">
						Are you sure you want to remove this item from your search history?
					</DialogContentText>
				</DialogContent>
				<DialogActions>
					<FluxButton type="text" onClick={() => setItemToDelete(null)}>
						Cancel
					</FluxButton>
					<FluxButton 
						type="filled" 
						tint="danger" 
						onClick={() => {
							if (itemToDelete) {
								searchHistoryUtils.removeFromHistory(itemToDelete);
								setSearchHistory(searchHistoryUtils.getHistory());
								setItemToDelete(null);
							}
						}}
					>
						Remove
					</FluxButton>
				</DialogActions>
			</Dialog>
		</Box>
	);
};

export default JiraSearch;
